import React, { useState, useEffect } from 'react';
import {
  MessageSquare,
  Search,
  Send,
  Smile,
  Paperclip,
  MoreVertical,
  Phone,
  Video,
  Image,
  File,
  Mic,
  CheckCheck,
  Clock,
  Circle,
  Star,
  Pin,
  Archive,
  Trash2,
  ChevronRight,
  Settings,
  Users,
  UserPlus,
  Bell,
  BellOff,
  BadgeCheck,
  ArrowLeft,
  Heart,
  Reply,
  Forward,
  Copy,
  Bookmark,
  X,
} from 'lucide-react';
import './MessagingMockupContent.css';

const MessagingMockupContent = ({ visibleItems, animationPhase, activeInteriorTab, onTabClick = () => {} }) => {
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [typingMessage, setTypingMessage] = useState('');
  const [showTypingIndicator, setShowTypingIndicator] = useState(false);
  const [visibleMessages, setVisibleMessages] = useState(0);

  // Avatar colors
  const avatarColors = [
    'linear-gradient(135deg, #6366f1 0%, #0284c7 100%)',
    'linear-gradient(135deg, #06b6d4 0%, #10b981 100%)',
    'linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)',
    'linear-gradient(135deg, #ec4899 0%, #0284c7 100%)',
    'linear-gradient(135deg, #10b981 0%, #06b6d4 100%)',
    'linear-gradient(135deg, #3b82f6 0%, #6366f1 100%)',
    'linear-gradient(135deg, #f97316 0%, #f59e0b 100%)',
    'linear-gradient(135deg, #0284c7 0%, #ec4899 100%)',
  ];

  // Conversations data - SYNCED with DeviceMockup dropdown
  // Total unread: Lena(3) + Idris(1) + Research Team(2) = 6
  const conversations = [
    {
      id: 1,
      user: 'Dr. Lena Hoffmann',
      initials: 'LH',
      avatar: null,
      lastMessage: 'Hey! Can we discuss the methodology section of the paper?',
      time: '2m ago',
      unread: 3,
      online: true,
      verified: true,
      pinned: true,
      typing: false,
      color: avatarColors[0],
    },
    {
      id: 2,
      user: 'Prof. Idris Malik',
      initials: 'IM',
      avatar: null,
      lastMessage: 'The results look very promising! 🎉',
      time: '15m ago',
      unread: 1,
      online: true,
      verified: true,
      pinned: false,
      typing: true,
      color: avatarColors[1],
    },
    {
      id: 3,
      user: 'Clara Whitfield',
      initials: 'CW',
      avatar: null,
      lastMessage: 'Thanks for the review feedback',
      time: '1h ago',
      unread: 0,
      online: false,
      verified: false,
      pinned: false,
      typing: false,
      color: avatarColors[2],
    },
    {
      id: 4,
      user: 'Dr. Tobias Richter',
      initials: 'TR',
      avatar: null,
      lastMessage: 'I\'ll send over the dataset tomorrow',
      time: '2h ago',
      unread: 0,
      online: true,
      verified: true,
      pinned: false,
      typing: false,
      color: avatarColors[3],
    },
    {
      id: 5,
      user: 'Research Team',
      initials: 'RT',
      avatar: null,
      lastMessage: 'Meeting scheduled for Friday at 3pm',
      time: '3h ago',
      unread: 2, // SYNCED: matches dropdown
      online: false,
      verified: false,
      pinned: true,
      isGroup: true,
      members: 8,
      color: avatarColors[5], // Changed to match dropdown blue-indigo
    },
    {
      id: 6,
      user: 'Dr. Naima Farooq',
      initials: 'NF',
      avatar: null,
      lastMessage: 'Great collaboration on the project!',
      time: '5h ago',
      unread: 0,
      online: false,
      verified: true,
      pinned: false,
      typing: false,
      color: avatarColors[4],
    },
    {
      id: 7,
      user: 'Climate AI Lab',
      initials: 'CL',
      avatar: null,
      lastMessage: 'New paper submission deadline extended',
      time: 'Yesterday',
      unread: 0, // Changed to 0 - not in dropdown unread list
      online: false,
      verified: false,
      pinned: false,
      isGroup: true,
      members: 15,
      color: avatarColors[6],
    },
    {
      id: 8,
      user: 'Dr. Hamza Qureshi',
      initials: 'HQ',
      avatar: null,
      lastMessage: 'Let me know when you\'re free to discuss',
      time: 'Yesterday',
      unread: 0,
      online: true,
      verified: false,
      pinned: false,
      typing: false,
      color: avatarColors[7],
    },
  ];

  // Chat messages for selected conversation
  const chatMessages = [
    {
      id: 1,
      sender: 'them',
      message: 'Hey! I just reviewed your latest draft on the neural network paper.',
      time: '10:30 AM',
      status: 'read',
    },
    {
      id: 2,
      sender: 'them',
      message: 'The methodology section is really well structured! 👏',
      time: '10:31 AM',
      status: 'read',
    },
    {
      id: 3,
      sender: 'me',
      message: 'Thanks! I spent a lot of time refining it based on your previous feedback.',
      time: '10:35 AM',
      status: 'read',
    },
    {
      id: 4,
      sender: 'them',
      message: 'I have a few suggestions for the results analysis. Can we discuss?',
      time: '10:36 AM',
      status: 'read',
    },
    {
      id: 5,
      sender: 'me',
      message: 'Absolutely! I\'m free now if you want to go over it.',
      time: '10:38 AM',
      status: 'read',
    },
    {
      id: 6,
      sender: 'them',
      message: 'Perfect! Let me share my screen...',
      time: '10:39 AM',
      status: 'read',
      hasAttachment: true,
      attachment: { type: 'file', name: 'analysis_notes.pdf', size: '2.4 MB' },
    },
    {
      id: 7,
      sender: 'me',
      message: 'Got it! Looking at the document now.',
      time: '10:42 AM',
      status: 'delivered',
    },
    {
      id: 8,
      sender: 'them',
      message: 'Hey! Can we discuss the methodology section of the paper?',
      time: '10:45 AM',
      status: 'delivered',
    },
  ];

  // Animation: Auto-select first conversation and show messages
  useEffect(() => {
    // Get the first available conversation for current tab
    const getFirstConversation = () => {
      switch (activeInteriorTab) {
        case 'online':
          return conversations.find(c => c.online);
        case 'unread':
          return conversations.find(c => c.unread > 0);
        case 'groups':
          return conversations.find(c => c.isGroup);
        default:
          return conversations[0];
      }
    };

    if (visibleItems >= 3 && !selectedConversation) {
      const firstConv = getFirstConversation();
      if (firstConv) {
        // Delay before auto-selecting so user can see the empty state
        const selectDelay = setTimeout(() => {
          setSelectedConversation(firstConv);
          
          // Animate messages appearing
          let msgCount = 0;
          const msgInterval = setInterval(() => {
            msgCount++;
            setVisibleMessages(msgCount);
            if (msgCount >= chatMessages.length) {
              clearInterval(msgInterval);
              // Show typing indicator after messages
              setTimeout(() => setShowTypingIndicator(true), 800);
              // Type a message
              setTimeout(() => {
                const reply = "That sounds great! Let's schedule a call.";
                let charIndex = 0;
                const typeInterval = setInterval(() => {
                  charIndex++;
                  setTypingMessage(reply.slice(0, charIndex));
                  if (charIndex >= reply.length) clearInterval(typeInterval);
                }, 40);
              }, 1500);
            }
          }, 200);
        }, 2000); // 2 second delay to show empty state
        
        return () => clearTimeout(selectDelay);
      }
    }
  }, [visibleItems, selectedConversation, activeInteriorTab]);

  // Reset animation when tab changes - but keep selected conversation if valid for new tab
  useEffect(() => {
    // Reset animation states for fresh start
    setVisibleMessages(0);
    setShowTypingIndicator(false);
    setTypingMessage('');
    
    // Check if current selection is still valid for the new tab
    if (selectedConversation) {
      let isStillValid = false;
      switch (activeInteriorTab) {
        case 'online':
          isStillValid = selectedConversation.online;
          break;
        case 'unread':
          isStillValid = selectedConversation.unread > 0;
          break;
        case 'groups':
          isStillValid = selectedConversation.isGroup;
          break;
        default: // 'all'
          isStillValid = true;
          break;
      }
      if (!isStillValid) {
        setSelectedConversation(null);
      }
    }
  }, [activeInteriorTab]);

  // Dynamic counts based on actual data
  const onlineCount = conversations.filter(c => c.online).length;
  const unreadCount = conversations.reduce((sum, c) => sum + c.unread, 0);
  const groupsCount = conversations.filter(c => c.isGroup).length;

  const tabs = [
    { id: 'all', label: 'All', count: null },
    { id: 'online', label: 'Online', count: onlineCount },
    { id: 'unread', label: 'Unread', count: unreadCount },
    { id: 'groups', label: 'Groups', count: groupsCount },
  ];

  // Filter conversations based on active tab
  const getFilteredConversations = () => {
    switch (activeInteriorTab) {
      case 'online':
        return conversations.filter(c => c.online);
      case 'unread':
        return conversations.filter(c => c.unread > 0);
      case 'groups':
        return conversations.filter(c => c.isGroup);
      default: // 'all'
        return conversations;
    }
  };

  const filteredConversations = getFilteredConversations();
  const pinnedConversations = filteredConversations.filter(c => c.pinned);
  const regularConversations = filteredConversations.filter(c => !c.pinned);

  return (
    <div className="messaging-mockup-content">
      {/* Header */}
      <div className={`messaging-mock-header ${visibleItems >= 1 ? 'visible' : ''}`}>
        <div className="messaging-header-icon">
          <MessageSquare size={16} />
        </div>
        <div className="messaging-header-text">
          <h2 className="messaging-header-title">Messages</h2>
          <p className="messaging-header-subtitle">Connect with researchers worldwide</p>
        </div>
        <div className="messaging-header-actions">
          <button className="messaging-compose-btn">
            <UserPlus size={12} />
            <span>New Chat</span>
          </button>
        </div>
      </div>

      {/* Main Content - Split View */}
      <div className="messaging-split-view">
        {/* Left Panel - Conversation List */}
        <div className={`messaging-conversations-panel ${visibleItems >= 1 ? 'visible' : ''}`}>
          {/* Tabs */}
          <div className="messaging-tabs">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                className={`messaging-tab ${activeInteriorTab === tab.id ? 'active' : ''}`}
                onClick={() => onTabClick(tab.id)}
              >
                {tab.label}
                {tab.count && <span className="messaging-tab-count">{tab.count}</span>}
              </button>
            ))}
          </div>

          {/* Search */}
          <div className="messaging-search">
            <Search size={12} />
            <input type="text" placeholder="Search conversations..." />
          </div>

          {/* Conversations List */}
          <div className="messaging-conversations-list">
            {/* Pinned Section */}
            {pinnedConversations.length > 0 && (
              <div className="messaging-section">
                <div className="messaging-section-header">
                  <Pin size={10} />
                  <span>Pinned</span>
                </div>
                {pinnedConversations.map((conv, index) => (
                  <div
                    key={conv.id}
                    className={`messaging-conversation-item ${selectedConversation?.id === conv.id ? 'selected' : ''} ${visibleItems > index ? 'visible' : ''}`}
                    style={{ animationDelay: `${index * 0.1}s` }}
                    onClick={() => setSelectedConversation(conv)}
                  >
                    <div className="messaging-conv-avatar-wrapper">
                      <div className="messaging-conv-avatar" style={{ background: conv.color }}>
                        {conv.isGroup ? <Users size={12} /> : conv.initials}
                      </div>
                      {conv.online && <div className="messaging-online-dot"></div>}
                    </div>
                    <div className="messaging-conv-content">
                      <div className="messaging-conv-header">
                        <span className="messaging-conv-name">
                          {conv.user}
                          {conv.verified && <BadgeCheck size={10} className="messaging-verified" />}
                        </span>
                        <span className="messaging-conv-time">{conv.time}</span>
                      </div>
                      <div className="messaging-conv-preview">
                        {conv.typing ? (
                          <span className="messaging-typing">
                            <span className="typing-dot"></span>
                            <span className="typing-dot"></span>
                            <span className="typing-dot"></span>
                            typing...
                          </span>
                        ) : (
                          <span className="messaging-last-msg">{conv.lastMessage}</span>
                        )}
                        {conv.unread > 0 && (
                          <span className="messaging-unread-badge">{conv.unread}</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* All Messages Section */}
            {regularConversations.length > 0 ? (
              <div className="messaging-section">
                <div className="messaging-section-header">
                  <MessageSquare size={10} />
                  <span>
                    {activeInteriorTab === 'online' && 'Online Now'}
                    {activeInteriorTab === 'unread' && 'Unread Messages'}
                    {activeInteriorTab === 'groups' && 'Group Chats'}
                    {activeInteriorTab === 'all' && 'All Messages'}
                  </span>
                </div>
                {regularConversations.map((conv, index) => (
                <div
                  key={conv.id}
                  className={`messaging-conversation-item ${selectedConversation?.id === conv.id ? 'selected' : ''} ${visibleItems > index + pinnedConversations.length ? 'visible' : ''}`}
                  style={{ animationDelay: `${(index + pinnedConversations.length) * 0.1}s` }}
                  onClick={() => setSelectedConversation(conv)}
                >
                  <div className="messaging-conv-avatar-wrapper">
                    <div className="messaging-conv-avatar" style={{ background: conv.color }}>
                      {conv.isGroup ? <Users size={12} /> : conv.initials}
                    </div>
                    {conv.online && <div className="messaging-online-dot"></div>}
                  </div>
                  <div className="messaging-conv-content">
                    <div className="messaging-conv-header">
                      <span className="messaging-conv-name">
                        {conv.user}
                        {conv.verified && <BadgeCheck size={10} className="messaging-verified" />}
                      </span>
                      <span className="messaging-conv-time">{conv.time}</span>
                    </div>
                    <div className="messaging-conv-preview">
                      {conv.typing ? (
                        <span className="messaging-typing">
                          <span className="typing-dot"></span>
                          <span className="typing-dot"></span>
                          <span className="typing-dot"></span>
                          typing...
                        </span>
                      ) : (
                        <span className="messaging-last-msg">{conv.lastMessage}</span>
                      )}
                      {conv.unread > 0 && (
                        <span className="messaging-unread-badge">{conv.unread}</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              </div>
            ) : filteredConversations.length === 0 && (
              <div className="messaging-empty-list">
                <div className="messaging-empty-list-icon">
                  {activeInteriorTab === 'online' && <Circle size={20} />}
                  {activeInteriorTab === 'unread' && <CheckCheck size={20} />}
                  {activeInteriorTab === 'groups' && <Users size={20} />}
                </div>
                <span className="messaging-empty-list-text">
                  {activeInteriorTab === 'online' && 'No contacts online'}
                  {activeInteriorTab === 'unread' && 'All caught up!'}
                  {activeInteriorTab === 'groups' && 'No group chats yet'}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Right Panel - Chat View */}
        <div className={`messaging-chat-panel ${selectedConversation ? 'has-conversation' : ''}`}>
          {selectedConversation ? (
            <>
              {/* Chat Header */}
              <div className="messaging-chat-header">
                <button className="messaging-back-btn">
                  <ArrowLeft size={14} />
                </button>
                <div className="messaging-chat-user">
                  <div className="messaging-chat-avatar" style={{ background: selectedConversation.color }}>
                    {selectedConversation.isGroup ? <Users size={14} /> : selectedConversation.initials}
                  </div>
                  <div className="messaging-chat-info">
                    <span className="messaging-chat-name">
                      {selectedConversation.user}
                      {selectedConversation.verified && <BadgeCheck size={11} className="messaging-verified" />}
                    </span>
                    <span className={`messaging-chat-status ${selectedConversation.online ? 'online' : ''}`}>
                      <Circle size={6} fill={selectedConversation.online ? '#10b981' : '#94a3b8'} />
                      {selectedConversation.online ? 'Online' : 'Offline'}
                      {selectedConversation.isGroup && ` • ${selectedConversation.members} members`}
                    </span>
                  </div>
                </div>
                <div className="messaging-chat-actions">
                  <button className="messaging-action-btn">
                    <Phone size={13} />
                  </button>
                  <button className="messaging-action-btn">
                    <Video size={13} />
                  </button>
                  <button className="messaging-action-btn">
                    <MoreVertical size={13} />
                  </button>
                </div>
              </div>

              {/* Chat Messages */}
              <div className="messaging-chat-messages">
                <div className="messaging-date-divider">
                  <span>Today</span>
                </div>
                {chatMessages.map((msg, index) => (
                  <div
                    key={msg.id}
                    className={`messaging-message ${msg.sender} ${visibleMessages > index ? 'visible' : ''}`}
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    {msg.sender === 'them' && (
                      <div className="messaging-msg-avatar" style={{ background: selectedConversation.color }}>
                        {selectedConversation.initials}
                      </div>
                    )}
                    <div className="messaging-msg-bubble">
                      <p className="messaging-msg-text">{msg.message}</p>
                      {msg.hasAttachment && (
                        <div className="messaging-attachment">
                          <File size={12} />
                          <div className="messaging-attachment-info">
                            <span className="messaging-attachment-name">{msg.attachment.name}</span>
                            <span className="messaging-attachment-size">{msg.attachment.size}</span>
                          </div>
                        </div>
                      )}
                      <div className="messaging-msg-meta">
                        <span className="messaging-msg-time">{msg.time}</span>
                        {msg.sender === 'me' && (
                          <CheckCheck size={10} className={`messaging-msg-status ${msg.status}`} />
                        )}
                      </div>
                    </div>
                  </div>
                ))}

                {/* Typing Indicator */}
                {showTypingIndicator && (
                  <div className="messaging-message them visible">
                    <div className="messaging-msg-avatar" style={{ background: selectedConversation.color }}>
                      {selectedConversation.initials}
                    </div>
                    <div className="messaging-typing-indicator">
                      <span className="typing-dot"></span>
                      <span className="typing-dot"></span>
                      <span className="typing-dot"></span>
                    </div>
                  </div>
                )}
              </div>

              {/* Chat Input */}
              <div className="messaging-chat-input">
                <div className="messaging-input-actions">
                  <button className="messaging-input-btn">
                    <Paperclip size={13} />
                  </button>
                  <button className="messaging-input-btn">
                    <Image size={13} />
                  </button>
                </div>
                <div className="messaging-input-wrapper">
                  <input
                    type="text"
                    placeholder="Type a message..."
                    value={typingMessage}
                    readOnly
                  />
                  <button className="messaging-emoji-btn">
                    <Smile size={14} />
                  </button>
                </div>
                <button className={`messaging-send-btn ${typingMessage ? 'active' : ''}`}>
                  <Send size={14} />
                </button>
              </div>
            </>
          ) : (
            /* Empty State */
            <div className="messaging-empty-state">
              <div className="messaging-empty-icon">
                <MessageSquare size={32} />
              </div>
              <h3 className="messaging-empty-title">No Conversation Selected</h3>
              <p className="messaging-empty-text">Select a conversation from the list to start messaging</p>
              <div className="messaging-empty-note">
                <span className="messaging-note-icon">💡</span>
                <span>You can only message your followers, people you follow, or collaborators</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Quick Actions Bar */}
      <div className={`messaging-quick-bar ${visibleItems >= 2 ? 'visible' : ''}`}>
        <div className="messaging-quick-stat">
          <div className="messaging-stat-icon messaging-stat-icon--blue">
            <MessageSquare size={10} />
          </div>
          <div className="messaging-stat-info">
            <span className="messaging-stat-value">24</span>
            <span className="messaging-stat-label">Conversations</span>
          </div>
        </div>
        <div className="messaging-quick-stat">
          <div className="messaging-stat-icon messaging-stat-icon--green">
            <Users size={10} />
          </div>
          <div className="messaging-stat-info">
            <span className="messaging-stat-value">8</span>
            <span className="messaging-stat-label">Online</span>
          </div>
        </div>
        <div className="messaging-quick-stat">
          <div className="messaging-stat-icon messaging-stat-icon--amber">
            <Bell size={10} />
          </div>
          <div className="messaging-stat-info">
            <span className="messaging-stat-value">11</span>
            <span className="messaging-stat-label">Unread</span>
          </div>
        </div>
        <div className="messaging-quick-stat">
          <div className="messaging-stat-icon messaging-stat-icon--pink">
            <Star size={10} />
          </div>
          <div className="messaging-stat-info">
            <span className="messaging-stat-value">5</span>
            <span className="messaging-stat-label">Starred</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MessagingMockupContent;
